import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShopLayout } from "@/components/ShopLayout";
import { api, ApiError } from "@/lib/api";
import { formatDate, formatPrice } from "@/lib/format";
import {
  VISITOR_PAYMENT_MODE_OPTIONS,
  chargeRuleLabel,
  visitorPaymentModeLabel,
  type VisitorPaymentMode,
  type VisitorPriceQuote,
  type VisitorRegistration,
} from "@/lib/visitor";

const EMPTY_FORM = {
  visit_date: new Date().toISOString().slice(0, 10),
  adults: "1",
  children: "0",
  extra_cars: "0",
  payment_mode: "annual_settlement" as VisitorPaymentMode,
  notes: "",
};

export function VisitorPage() {
  const [registrations, setRegistrations] = useState<VisitorRegistration[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [quote, setQuote] = useState<VisitorPriceQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const partyValid = useMemo(() => {
    const adults = Number(form.adults) || 0;
    const children = Number(form.children) || 0;
    const extraCars = Number(form.extra_cars) || 0;
    return adults + children + extraCars > 0;
  }, [form.adults, form.children, form.extra_cars]);

  function loadRegistrations() {
    setListLoading(true);
    setListError(null);
    void api
      .get<VisitorRegistration[]>("/api/v1/public/shop/visitors")
      .then(setRegistrations)
      .catch((err) => {
        setRegistrations([]);
        setListError(err instanceof ApiError ? err.message : "Anmeldungen konnten nicht geladen werden.");
      })
      .finally(() => setListLoading(false));
  }

  useEffect(() => {
    loadRegistrations();
  }, []);

  useEffect(() => {
    if (!partyValid) {
      setQuote(null);
      setQuoteError(null);
      return;
    }

    const timer = window.setTimeout(() => {
      setQuoteLoading(true);
      setQuoteError(null);
      void api
        .post<VisitorPriceQuote>("/api/v1/public/shop/visitors/quote", {
          visit_date: form.visit_date,
          adults: Number(form.adults) || 0,
          children: Number(form.children) || 0,
          extra_cars: Number(form.extra_cars) || 0,
        })
        .then(setQuote)
        .catch((err) => {
          setQuote(null);
          setQuoteError(err instanceof ApiError ? err.message : "Preis konnte nicht berechnet werden.");
        })
        .finally(() => setQuoteLoading(false));
    }, 300);

    return () => window.clearTimeout(timer);
  }, [form.visit_date, form.adults, form.children, form.extra_cars, partyValid]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setSubmitting(true);
    try {
      const result = await api.post<{ message: string }>("/api/v1/public/shop/visitors", {
        visit_date: form.visit_date,
        adults: Number(form.adults) || 0,
        children: Number(form.children) || 0,
        extra_cars: Number(form.extra_cars) || 0,
        payment_mode: form.payment_mode,
        notes: form.notes.trim() || null,
      });
      setSuccessMessage(result.message);
      setForm(EMPTY_FORM);
      setQuote(null);
      loadRegistrations();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Fehler beim Anmelden");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ShopLayout>
      <div className="shop-page mx-auto max-w-3xl space-y-8">
        <div>
          <Link to="/" className="text-sm text-brand-600 hover:text-brand-700">
            ← Zurück zum Shop
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-slate-900">Besucher anmelden</h1>
          <p className="mt-1 text-sm text-slate-600">
            Melden Sie Besucher für Ihren Dauercamper-Vertrag an. Die Gebühren richten sich nach den
            hinterlegten Preisregeln.
          </p>
        </div>

        <form onSubmit={(e) => void onSubmit(e)} className="shop-card space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Neue Anmeldung</h2>

          <label className="flex flex-col gap-1 text-sm">
            Besuchsdatum
            <input
              type="date"
              className="rounded-lg border border-slate-300 px-3 py-2"
              required
              value={form.visit_date}
              onChange={(e) => setForm((f) => ({ ...f, visit_date: e.target.value }))}
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              Besucher Erwachsene
              <input
                type="number"
                min={0}
                className="rounded-lg border border-slate-300 px-3 py-2"
                value={form.adults}
                onChange={(e) => setForm((f) => ({ ...f, adults: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Besucher Kinder
              <input
                type="number"
                min={0}
                className="rounded-lg border border-slate-300 px-3 py-2"
                value={form.children}
                onChange={(e) => setForm((f) => ({ ...f, children: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Zusätzliche Autos
              <input
                type="number"
                min={0}
                className="rounded-lg border border-slate-300 px-3 py-2"
                value={form.extra_cars}
                onChange={(e) => setForm((f) => ({ ...f, extra_cars: e.target.value }))}
              />
            </label>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-900">Abrechnung</legend>
            {VISITOR_PAYMENT_MODE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer gap-3 rounded-lg border border-slate-200 p-3 text-sm"
              >
                <input
                  type="radio"
                  name="payment_mode"
                  className="mt-1"
                  checked={form.payment_mode === option.value}
                  onChange={() => setForm((f) => ({ ...f, payment_mode: option.value }))}
                />
                <span>
                  <span className="font-medium text-slate-900">{option.label}</span>
                  <span className="mt-0.5 block text-slate-500">{option.description}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <label className="flex flex-col gap-1 text-sm">
            Notiz (optional)
            <textarea
              className="min-h-[4rem] rounded-lg border border-slate-300 px-3 py-2"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </label>

          {quoteLoading ? (
            <p className="text-sm text-slate-500">Preis wird berechnet…</p>
          ) : quoteError ? (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{quoteError}</p>
          ) : quote ? (
            <div className="rounded-lg border border-slate-200 p-3 text-sm">
              <p className="mb-2 font-medium text-slate-900">Voraussichtliche Gebühren</p>
              <ul className="space-y-1 text-slate-600">
                {quote.lines.map((line) => (
                  <li key={`${line.rule_type}-${line.article_id}`} className="flex justify-between gap-4">
                    <span>
                      {chargeRuleLabel(line.rule_type)}: {line.quantity} × {line.article_name}
                    </span>
                    <span className="tabular-nums text-slate-900">{formatPrice(line.line_total)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 flex justify-between gap-4 border-t border-slate-200 pt-2 font-medium text-slate-900">
                <span>Gesamt (brutto)</span>
                <span className="tabular-nums">{formatPrice(quote.total)}</span>
              </p>
            </div>
          ) : null}

          {successMessage ? (
            <p className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{successMessage}</p>
          ) : null}
          {error ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}

          <button
            type="submit"
            disabled={!partyValid || !!quoteError || quoteLoading || submitting}
            className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Wird angemeldet…" : "Besucher anmelden"}
          </button>
        </form>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">Ihre Anmeldungen</h2>
          {listLoading ? (
            <p className="text-sm text-slate-500">Anmeldungen werden geladen…</p>
          ) : listError ? (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{listError}</p>
          ) : registrations.length === 0 ? (
            <div className="shop-card text-center text-sm text-slate-600">
              Noch keine Besucheranmeldungen.
            </div>
          ) : (
            <ul className="space-y-3">
              {registrations.map((registration) => (
                <li key={registration.id} className="shop-card text-sm">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{formatDate(registration.visit_date)}</p>
                      <p className="mt-1 text-slate-600">
                        {registration.adults} Erwachsene, {registration.children} Kinder
                        {registration.extra_cars > 0 ? `, ${registration.extra_cars} Auto(s)` : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold tabular-nums text-slate-900">
                        {formatPrice(registration.total_amount)}
                      </p>
                      <p className="text-slate-500">{visitorPaymentModeLabel(registration.payment_mode)}</p>
                    </div>
                  </div>
                  {registration.invoice_number ? (
                    <p className="mt-2 text-slate-500">Rechnung {registration.invoice_number}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </ShopLayout>
  );
}
